import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Stack,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";

interface AuditLogDetailDialogProps {
  open: boolean;
  onClose: () => void;
  log: any | null;
}

const AuditLogDetailDialog: React.FC<AuditLogDetailDialogProps> = ({
  open,
  onClose,
  log,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!log) return null;

  const renderSection = (title: string, content: React.ReactNode) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      {content}
    </Box>
  );

  const renderJSONData = (data: any) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          bgcolor: "grey.50",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <pre
          style={{
            margin: 0,
            fontSize: "0.8125rem",
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={isMobile ? { px: 2, py: 1.5 } : undefined}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Typography variant="h6">Audit Log Details</Typography>
          <Chip
            label={log.success ? "Success" : "Failed"}
            size="small"
            color={log.success ? "success" : "error"}
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={isMobile ? { px: 2 } : undefined}>
        <Stack spacing={2}>
          {/* Basic Info */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {renderSection(
              "Timestamp",
              <Typography variant="body1">
                {format(new Date(log.createdAt), "MMMM dd, yyyy HH:mm:ss")}
              </Typography>,
            )}

            {renderSection(
              "User",
              <Typography variant="body1">
                {log.userEmail || "System"}
              </Typography>,
            )}

            {renderSection("Action", <Chip label={log.action} size="small" />)}

            {renderSection(
              "Entity Type",
              <Typography variant="body1">{log.entityType}</Typography>,
            )}

            {log.entityName &&
              renderSection(
                "Entity Name",
                <Typography variant="body1">{log.entityName}</Typography>,
              )}

            {log.entityId &&
              renderSection(
                "Entity ID",
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {log.entityId}
                </Typography>,
              )}

            {log.description &&
              renderSection(
                "Description",
                <Typography variant="body1">{log.description}</Typography>,
              )}
          </Box>

          {/* Request Info */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Request Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {log.ipAddress &&
              renderSection(
                "IP Address",
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {log.ipAddress}
                </Typography>,
              )}

            {log.endpoint &&
              renderSection(
                "Endpoint",
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  {log.httpMethod && (
                    <Chip label={log.httpMethod} size="small" />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                  >
                    {log.endpoint}
                  </Typography>
                </Stack>,
              )}

            {log.userAgent &&
              renderSection(
                "User Agent",
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  {log.userAgent}
                </Typography>,
              )}

            {log.jurisdiction &&
              renderSection(
                "Jurisdiction",
                <Chip
                  label={log.jurisdiction}
                  size="small"
                  variant="outlined"
                />,
              )}
          </Box>

          {/* Data Changes */}
          {(log.oldValues || log.newValues || log.changes) && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Data Changes
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {log.oldValues &&
                renderSection("Old Values", renderJSONData(log.oldValues))}
              {log.newValues &&
                renderSection("New Values", renderJSONData(log.newValues))}
              {log.changes &&
                renderSection("Changes", renderJSONData(log.changes))}
            </Box>
          )}

          {/* Email Diagnostics (special rendering for EMAIL_SEND actions) */}
          {log.metadata?.emailDiagnostics && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Email Delivery Diagnostics
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {renderSection(
                "Recipient",
                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                  {log.metadata.emailDiagnostics.recipientEmail}
                </Typography>,
              )}

              {renderSection(
                "Subject",
                <Typography variant="body1">
                  {log.metadata.emailDiagnostics.subject}
                </Typography>,
              )}

              {log.metadata.emailDiagnostics.template &&
                renderSection(
                  "Template",
                  <Chip
                    label={log.metadata.emailDiagnostics.template}
                    size="small"
                    variant="outlined"
                  />,
                )}

              {renderSection(
                "From",
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {log.metadata.emailDiagnostics.fromAddress}
                </Typography>,
              )}

              {renderSection(
                "SES Region",
                <Chip
                  label={log.metadata.emailDiagnostics.sesRegion}
                  size="small"
                  variant="outlined"
                />,
              )}

              {renderSection(
                "Timestamp",
                <Typography variant="body2">
                  {log.metadata.emailDiagnostics.timestamp}
                </Typography>,
              )}

              {/* Suppression Check */}
              {log.metadata.emailDiagnostics.suppressionCheck && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: log.metadata.emailDiagnostics.suppressionCheck
                      .wasSuppressed
                      ? "warning.50"
                      : "success.50",
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Suppression List Check
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      Checked:{" "}
                      <Chip
                        label={
                          log.metadata.emailDiagnostics.suppressionCheck.checked
                            ? "Yes"
                            : "No"
                        }
                        size="small"
                      />
                    </Typography>
                    <Typography variant="body2">
                      Was Suppressed:{" "}
                      <Chip
                        label={
                          log.metadata.emailDiagnostics.suppressionCheck
                            .wasSuppressed
                            ? "YES"
                            : "No"
                        }
                        size="small"
                        color={
                          log.metadata.emailDiagnostics.suppressionCheck
                            .wasSuppressed
                            ? "warning"
                            : "success"
                        }
                      />
                    </Typography>
                    {log.metadata.emailDiagnostics.suppressionCheck.reason && (
                      <Typography variant="body2">
                        Reason:{" "}
                        <strong>
                          {
                            log.metadata.emailDiagnostics.suppressionCheck
                              .reason
                          }
                        </strong>
                      </Typography>
                    )}
                    {log.metadata.emailDiagnostics.suppressionCheck
                      .suppressedSince && (
                      <Typography variant="body2">
                        Suppressed Since:{" "}
                        {
                          log.metadata.emailDiagnostics.suppressionCheck
                            .suppressedSince
                        }
                      </Typography>
                    )}
                    {log.metadata.emailDiagnostics.suppressionCheck
                      .wasSuppressed && (
                      <Typography variant="body2">
                        Auto-Removed:{" "}
                        <Chip
                          label={
                            log.metadata.emailDiagnostics.suppressionCheck
                              .removed
                              ? "Yes"
                              : "FAILED"
                          }
                          size="small"
                          color={
                            log.metadata.emailDiagnostics.suppressionCheck
                              .removed
                              ? "success"
                              : "error"
                          }
                        />
                      </Typography>
                    )}
                    {log.metadata.emailDiagnostics.suppressionCheck
                      .removalError && (
                      <Typography variant="body2" color="error">
                        Removal Error:{" "}
                        {
                          log.metadata.emailDiagnostics.suppressionCheck
                            .removalError
                        }
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}

              {/* SES Response */}
              {log.metadata.emailDiagnostics.sesResponse && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    SES Response
                  </Typography>
                  <Stack spacing={0.5}>
                    {log.metadata.emailDiagnostics.sesResponse.messageId && (
                      <Typography variant="body2">
                        Message ID:{" "}
                        <code>
                          {log.metadata.emailDiagnostics.sesResponse.messageId}
                        </code>
                      </Typography>
                    )}
                    {log.metadata.emailDiagnostics.sesResponse
                      .httpStatusCode && (
                      <Typography variant="body2">
                        HTTP Status:{" "}
                        <Chip
                          label={
                            log.metadata.emailDiagnostics.sesResponse
                              .httpStatusCode
                          }
                          size="small"
                          color={
                            log.metadata.emailDiagnostics.sesResponse
                              .httpStatusCode === 200
                              ? "success"
                              : "error"
                          }
                        />
                      </Typography>
                    )}
                    {log.metadata.emailDiagnostics.sesResponse.requestId && (
                      <Typography variant="body2">
                        Request ID:{" "}
                        <code style={{ fontSize: "0.75rem" }}>
                          {log.metadata.emailDiagnostics.sesResponse.requestId}
                        </code>
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}

              {/* Error Details */}
              {log.metadata.emailDiagnostics.error && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 2, bgcolor: "error.50" }}
                >
                  <Typography variant="subtitle2" gutterBottom color="error">
                    Send Error
                  </Typography>
                  <Stack spacing={0.5}>
                    {log.metadata.emailDiagnostics.error.name && (
                      <Typography variant="body2">
                        Error Type:{" "}
                        <strong>
                          {log.metadata.emailDiagnostics.error.name}
                        </strong>
                      </Typography>
                    )}
                    <Typography variant="body2">
                      Message: {log.metadata.emailDiagnostics.error.message}
                    </Typography>
                    {log.metadata.emailDiagnostics.error.code && (
                      <Typography variant="body2">
                        Code:{" "}
                        <code>{log.metadata.emailDiagnostics.error.code}</code>
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}
            </Box>
          )}

          {/* Metadata (non-email) */}
          {log.metadata && !log.metadata.emailDiagnostics && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Metadata
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderJSONData(log.metadata)}
            </Box>
          )}

          {/* Error Info */}
          {!log.success && log.errorMessage && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Error Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "error.light",
                  color: "error.contrastText",
                }}
              >
                <Typography variant="body2">{log.errorMessage}</Typography>
              </Paper>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={
          isMobile
            ? { px: 2, pb: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }
            : undefined
        }
      >
        <Button onClick={onClose} fullWidth={isMobile}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogDetailDialog;
